import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import {
  AlertTriangle, Check, ChevronRight, Crosshair, Loader2, MapPin, Plus, Shapes,
} from 'lucide-react'
import { Field, Modal } from '@/shared/ui/Modal'
import { Note } from '@/shared/ui/states'
import { ApiError, explain } from '@/shared/api/problem'
import { useDebounced } from '@/shared/hooks/useDebounced'
import { UnitMap } from '@/shared/map/UnitMap'
import { formatDms, outsideKenya, KE_BOUNDS } from '@/shared/map/geo'
import { cn } from '@/lib/utils'
import { codeAvailable, createUnit } from './api'
import {
  UNIT_TYPE_LABEL, childTypeOf, geometryKindOf,
  type CodeAvailability, type StationLocationTypeCode, type UnitResponse,
} from '@/shared/api/types'

const STATION_LOCATION_TYPES: StationLocationTypeCode[] = [
  'SCHOOL', 'CHURCH', 'MOSQUE', 'SOCIAL_HALL', 'CHIEF_CAMP', 'MARKET', 'TENT', 'OTHER',
]

/**
 * Create New Electoral Unit.
 *
 * Three things shape this form, and they are all consequences of the domain
 * rather than layout preferences:
 *
 * 1. **The type is derived, never chosen.** The database enforces parent-type
 *    validity through a trigger (`V2_020_005`), so a type dropdown would only be
 *    a way to earn a constraint violation. You create a ward *under* a
 *    constituency; the form tells you that is what you are doing.
 *
 * 2. **Geometry follows the type.** A polling station is a POINT: you place it on
 *    a map, and the coordinates are its authoritative geometry. A county,
 *    constituency or ward is a BOUNDARY: its real geometry is a polygon uploaded
 *    afterwards, and any pin entered here is an explicit placeholder. The form
 *    says which, because a ward created with only a pin is half-finished and the
 *    user should leave knowing that.
 *
 * 3. **Only fields the backend stores are rendered.** All seven
 *    `geography_station_detail` columns are now accepted through the `station`
 *    block, so all seven are offered — but only for a station. Sending the block
 *    on a ward is a 400 by design: a user who filled in "has power" for an area
 *    should be told it does not apply, not have it silently discarded.
 */
export function CreateUnitDialog({
  open, parent, onClose, onCreated,
}: {
  open: boolean
  parent: UnitResponse | null
  onClose: () => void
  onCreated: (u: UnitResponse, keepOpen: boolean) => void
}) {
  const childType = childTypeOf(parent?.unitType)
  const kind = childType ? geometryKindOf(childType) : 'BOUNDARY'
  const isStation = childType === 'POLLING_STATION'

  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [region, setRegion] = useState('')
  const [lat, setLat] = useState<number | ''>('')
  const [lon, setLon] = useState<number | ''>('')
  const [registered, setRegistered] = useState<number | ''>('')
  const [locationType, setLocationType] = useState<StationLocationTypeCode>('SCHOOL')
  const [population, setPopulation] = useState<number | ''>('')
  const [streams, setStreams] = useState<number | ''>('')
  const [hasPower, setHasPower] = useState<boolean | null>(null)
  const [hasNetwork, setHasNetwork] = useState<boolean | null>(null)
  const [stepFree, setStepFree] = useState<boolean | null>(null)
  const [notes, setNotes] = useState('')
  const [again, setAgain] = useState(false)

  const [busy, setBusy] = useState(false)
  const [locating, setLocating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const reset = useCallback((keepRegion: boolean) => {
    setCode(''); setName('')
    if (!keepRegion) setRegion('')
    setLat(''); setLon(''); setRegistered(''); setPopulation('')
    setLocationType('SCHOOL'); setStreams('')
    setHasPower(null); setHasNetwork(null); setStepFree(null); setNotes('')
    setError(null); setFieldErrors({})
  }, [])

  useEffect(() => { if (open) { reset(false); setAgain(false) } }, [open, reset])

  // ── Code availability ──────────────────────────────────────────────────────
  // `GET /units/code-available` answers two different collisions. TAKEN is the
  // obvious one. LTREE_COLLISION is not: the path builder replaces every
  // non-alphanumeric with `_`, so `04.701` and `04-701` produce the same ltree
  // label and the second create violates the path UNIQUE — a 409 with a raw
  // Postgres message, unless it is caught here first.
  const debouncedCode = useDebounced(code.trim(), 400)
  const [taken, setTaken] = useState<CodeAvailability | null>(null)
  useEffect(() => {
    if (!open || !childType || !parent || debouncedCode.length < 1) { setTaken(null); return }
    let cancelled = false
    codeAvailable(childType, debouncedCode, parent.id)
      .then((r) => { if (!cancelled) setTaken(r.available ? null : r) })
      .catch(() => { if (!cancelled) setTaken(null) })
    return () => { cancelled = true }
  }, [debouncedCode, childType, parent, open])

  function useMyLocation() {
    if (!('geolocation' in navigator)) {
      setError('This browser cannot report a location.')
      return
    }
    setLocating(true)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(Number(pos.coords.latitude.toFixed(6)))
        setLon(Number(pos.coords.longitude.toFixed(6)))
        setLocating(false)
      },
      (err) => {
        setError(err.code === err.PERMISSION_DENIED
          ? 'Location permission was denied. Place the pin on the map, or type the coordinates.'
          : 'Could not get a location fix.')
        setLocating(false)
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    )
  }

  const pickOnMap = useCallback((la: number, lo: number) => { setLat(la); setLon(lo) }, [])

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!childType || !parent) return
    setBusy(true); setError(null); setFieldErrors({})
    try {
      const created = await createUnit({
        unitType: childType,
        code: code.trim(),
        name: name.trim(),
        parentId: parent.id,
        region: region.trim() || null,
        latitude: lat === '' ? null : lat,
        longitude: lon === '' ? null : lon,
        // Only a station carries its own register — a parent's is rolled up from
        // the stations beneath it, and both create and update reject it outright.
        registeredVoters: isStation && registered !== '' ? registered : null,
        population: population === '' ? null : population,
        // The `station` block goes only on a station; the backend 400s it anywhere
        // else. Nulls inside mean "not supplied", so an untouched tri-state stays
        // unset rather than being written as false. The deprecated top-level
        // `locationType` is deliberately not sent — one source is one source.
        station: isStation ? {
          locationType,
          streamCount: streams === '' ? null : streams,
          hasPower, hasNetwork, disabledAccess: stepFree,
          notes: notes.trim() || null,
        } : null,
      })
      onCreated(created, again)
      if (again) reset(true)
      else onClose()
    } catch (err) {
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors())
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  const warn = outsideKenya(lat, lon)
  const hasPoint = lat !== '' && lon !== ''
  const canSubmit = Boolean(code.trim() && name.trim() && !busy && !taken)

  const breadcrumb = useMemo(() => {
    if (!parent) return null
    return (
      <span className="inline-flex items-center gap-1 min-w-0 flex-wrap">
        <span className="truncate">{parent.name}</span>
        <span className="font-mono text-erp-xs text-muted-foreground">{parent.code}</span>
        <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
        <span className="px-1.5 py-0.5 rounded border border-chocolate/40 bg-chocolate-soft/40 text-chocolate font-medium">
          new {childType ? (UNIT_TYPE_LABEL[childType] ?? childType).toLowerCase() : 'unit'}
        </span>
      </span>
    )
  }, [parent, childType])

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="lg"
      title="Create new electoral unit"
      description={parent ? undefined : 'Select a parent unit first.'}
      footer={childType ? (
        <>
          <label className="mr-auto inline-flex items-center gap-2 text-erp-xs text-muted-foreground cursor-pointer">
            <input type="checkbox" checked={again} onChange={(e) => setAgain(e.target.checked)} />
            Keep open to add another
          </label>
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            form="create-unit-form"
            className="erp-btn erp-btn-primary"
            disabled={!canSubmit}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Create {childType ? (UNIT_TYPE_LABEL[childType] ?? '').toLowerCase() : 'unit'}
          </button>
        </>
      ) : <button type="button" className="erp-btn" onClick={onClose}>Close</button>}
    >
      {!childType ? (
        <Note tone="info">
          A polling station is the bottom of the hierarchy — nothing can be created
          beneath it.
        </Note>
      ) : (
        <form id="create-unit-form" onSubmit={submit} className="space-y-5">
          {error && <Note tone="bad">{error}</Note>}

          {/* ── Hierarchy ─────────────────────────────────────────────────── */}
          <Section
            title="Hierarchy"
            hint="The type is decided by the parent — the database enforces it, so it is not a choice."
          >
            <div className="px-3 py-2 rounded-md border border-border bg-muted/30 text-erp-sm">
              {breadcrumb}
            </div>
          </Section>

          {/* ── Identity ──────────────────────────────────────────────────── */}
          <Section title="Identity">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" required error={fieldErrors.name}>
                <input value={name} onChange={(e) => setName(e.target.value)}
                       className="erp-input w-full" maxLength={160} required autoFocus />
              </Field>
              <Field
                label="IEBC code"
                required
                hint={taken ? undefined : 'Unique within this unit type. It cannot be changed later.'}
                error={fieldErrors.code ?? (taken
                  ? taken.reason === 'LTREE_COLLISION'
                    ? `This collides with “${taken.existingUnitName}”: punctuation is stripped when the path is built, so both become the same label. Change a digit, not the separator.`
                    : `Code is already used by “${taken.existingUnitName}”.`
                  : undefined)}
              >
                <input value={code} onChange={(e) => setCode(e.target.value)}
                       className={cn('erp-input w-full font-mono', taken && 'border-red-500')}
                       maxLength={32} required />
              </Field>
              {!isStation && (
                <>
                  <Field
                    label="Region"
                    error={fieldErrors.region}
                    hint="Inherited from the parent when left blank."
                  >
                    <input value={region} onChange={(e) => setRegion(e.target.value)}
                           className="erp-input w-full" maxLength={48}
                           placeholder={parent?.region ?? 'Nairobi, Coast…'} />
                  </Field>
                  <Field
                    label="Population"
                    hint="Census figure, if you have one. Registered voters are rolled up from stations."
                  >
                    <input type="number" min={0} value={population}
                           onChange={(e) => setPopulation(e.target.value === '' ? '' : Number(e.target.value))}
                           className="erp-input w-full" />
                  </Field>
                </>
              )}
              {isStation && (
                <Field
                  label="Registered voters"
                  error={fieldErrors.registeredVoters}
                  hint="Only a station holds its own register — parents are rolled up from these."
                >
                  <input
                    type="number" min={0}
                    value={registered}
                    onChange={(e) => setRegistered(e.target.value === '' ? '' : Number(e.target.value))}
                    className="erp-input w-full"
                  />
                </Field>
              )}
            </div>
          </Section>

          {/* ── Location ──────────────────────────────────────────────────── */}
          <Section
            title="Location"
            badge={
              <span className="inline-flex items-center gap-1 text-erp-xs text-muted-foreground">
                <Shapes className="h-3 w-3" />
                {kind === 'POINT' ? 'Point-based' : 'Boundary-based'}
              </span>
            }
            hint={kind === 'POINT'
              ? 'A polling station is a place. Its coordinates are its geometry — click the map to place it.'
              : `A ${(UNIT_TYPE_LABEL[childType] ?? '').toLowerCase()} is an area. Its real geometry is a boundary polygon, uploaded on the unit’s Boundary tab after it exists. Anything set here is a placeholder pin.`}
          >
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,18rem)] items-start">
              <UnitMap
                mode={kind === 'POINT' ? 'point' : 'boundary'}
                lat={lat === '' ? null : lat}
                lon={lon === '' ? null : lon}
                editable={kind === 'POINT'}
                onPick={pickOnMap}
                label={name || 'new unit'}
                className="h-56"
              />

              <div className="space-y-3">
                <div className="grid gap-3 grid-cols-2 lg:grid-cols-1">
                  <Field label="Latitude" error={fieldErrors.latitude}>
                    <input
                      type="number" step="0.000001"
                      value={lat}
                      onChange={(e) => setLat(e.target.value === '' ? '' : Number(e.target.value))}
                      className="erp-input w-full font-mono"
                      placeholder="-1.286389"
                    />
                  </Field>
                  <Field label="Longitude" error={fieldErrors.longitude}>
                    <input
                      type="number" step="0.000001"
                      value={lon}
                      onChange={(e) => setLon(e.target.value === '' ? '' : Number(e.target.value))}
                      className="erp-input w-full font-mono"
                      placeholder="36.817223"
                    />
                  </Field>
                </div>

                {hasPoint && !warn && (
                  <p className="text-erp-xs text-muted-foreground font-mono">
                    {formatDms(lat as number, 'lat')} {formatDms(lon as number, 'lon')}
                  </p>
                )}

                <button type="button" className="erp-btn w-full" onClick={useMyLocation}
                        disabled={locating}>
                  {locating
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Crosshair className="h-3.5 w-3.5" />}
                  Use my location
                </button>

                {hasPoint && (
                  <button type="button" className="erp-btn w-full"
                          onClick={() => { setLat(''); setLon('') }}>
                    Clear the pin
                  </button>
                )}
              </div>
            </div>

            {warn && (
              <Note tone="bad">
                Those coordinates are outside Kenya. Latitude and longitude may be
                swapped — Kenya runs {KE_BOUNDS.minLat} to {KE_BOUNDS.maxLat} latitude
                and {KE_BOUNDS.minLon} to {KE_BOUNDS.maxLon} longitude. The backend
                accepts them on a single create, but the bulk importer rejects the same
                pair, so this is worth fixing now.
              </Note>
            )}

            {kind === 'POINT' && !hasPoint && (
              <p className="text-erp-xs text-muted-foreground flex items-start gap-1.5">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                Without coordinates this station will not appear in nearby search or on
                any map. You can add them later, but nobody will go looking.
              </p>
            )}
          </Section>

          {/* ── Facility ──────────────────────────────────────────────────── */}
          {isStation && (
            <Section
              title="Facility"
              hint="Everything here is optional. A blank tri-state means “not recorded”, which is different from “no”."
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Location type" error={fieldErrors.locationType}>
                  <select value={locationType}
                          onChange={(e) => setLocationType(e.target.value as StationLocationTypeCode)}
                          className="erp-input w-full">
                    {STATION_LOCATION_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t.charAt(0) + t.slice(1).replace(/_/g, ' ').toLowerCase()}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Streams" hint="Separate queues at one venue.">
                  <input type="number" min={1} max={50} value={streams}
                         onChange={(e) => setStreams(e.target.value === '' ? '' : Number(e.target.value))}
                         className="erp-input w-full" />
                </Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <TriState label="Mains power" value={hasPower} onChange={setHasPower} />
                <TriState label="Mobile network" value={hasNetwork} onChange={setHasNetwork} />
                <TriState label="Step-free access" value={stepFree} onChange={setStepFree} />
              </div>
              <Field label="Notes">
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                          maxLength={2000} rows={2} className="erp-input w-full !h-auto py-2" />
              </Field>
            </Section>
          )}
        </form>
      )}
    </Modal>
  )
}

/**
 * Yes / No / not recorded.
 *
 * A checkbox cannot express the third state, and defaulting an unknown to `false`
 * is how "we have not checked whether this station has power" becomes "this
 * station has no power" in a report six months later.
 */
function TriState({
  label, value, onChange,
}: {
  label: string
  value: boolean | null
  onChange: (v: boolean | null) => void
}) {
  const options: [string, boolean | null][] = [['Yes', true], ['No', false], ['—', null]]
  return (
    <Field label={label}>
      <div className="flex gap-1">
        {options.map(([text, v]) => (
          <button
            key={text}
            type="button"
            onClick={() => onChange(v)}
            className={cn(
              'erp-btn flex-1 justify-center',
              value === v && 'border-chocolate bg-chocolate-soft/50 text-chocolate font-medium',
            )}
          >
            {text}
          </button>
        ))}
      </div>
    </Field>
  )
}

function Section({
  title, hint, badge, children,
}: {
  title: string
  hint?: string
  badge?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-3 border-b border-border pb-1.5">
        <h4 className="text-erp-sm font-semibold tracking-tight">{title}</h4>
        {badge}
      </div>
      {hint && <p className="text-erp-xs text-muted-foreground">{hint}</p>}
      {children}
    </section>
  )
}

/** Shown after a create when "keep open" is on, so the run of units is visible. */
export function CreatedTrail({ items }: { items: UnitResponse[] }) {
  if (items.length === 0) return null
  return (
    <div className="flex items-center gap-1.5 flex-wrap text-erp-xs text-muted-foreground">
      <Check className="h-3 w-3 text-emerald-600" />
      Created:
      {items.slice(-6).map((u) => (
        <span key={u.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-border">
          <MapPin className="h-2.5 w-2.5" />
          {u.name}
        </span>
      ))}
      {items.length > 6 && <span>+{items.length - 6} more</span>}
    </div>
  )
}
